/* EMI Calculator Logic — Amortization, Interactive Math, Charts & CSV Export */

let emiDonutChart = null;
let emiGrowthChart = null;

function getEmiInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    amount: getVal('emiAmount', 2500000),
    rate: getVal('emiRate', 8.5),
    tenure: getVal('emiTenure', 20),
  };
}

function updateEmiUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('amount', params.amount);
    url.searchParams.set('rate', params.rate);
    url.searchParams.set('tenure', params.tenure);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseEmiUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const amount = searchParams.get('amount');
  const rate = searchParams.get('rate');
  const tenure = searchParams.get('tenure');

  return {
    amount: amount !== null ? +amount : null,
    rate: rate !== null ? +rate : null,
    tenure: tenure !== null ? +tenure : null,
  };
}

function calculateEmi(params) {
  const { amount, rate, tenure } = params;

  const n = tenure * 12;
  const r = rate / 12 / 100;

  let monthlyEmi = 0;
  if (r > 0) {
    monthlyEmi = (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  } else {
    monthlyEmi = amount / n;
  }

  const totalPayment = monthlyEmi * n;
  const totalInterest = Math.max(0, totalPayment - amount);

  const schedule = [];
  let balance = amount;
  let cumInterest = 0;

  for (let yr = 1; yr <= tenure; yr++) {
    const openingBalance = balance;
    let annualInterest = 0;
    let annualPrincipal = 0;

    for (let m = 1; m <= 12; m++) {
      const interestForMonth = balance * r;
      const principalForMonth = Math.min(balance, monthlyEmi - interestForMonth);

      balance = Math.max(0, balance - principalForMonth);
      annualInterest += interestForMonth;
      annualPrincipal += principalForMonth;
      cumInterest += interestForMonth;
    }

    schedule.push({
      year: yr,
      openingBalance: Math.round(openingBalance),
      annualEmi: Math.round(annualInterest + annualPrincipal),
      annualInterest: Math.round(annualInterest),
      annualPrincipal: Math.round(annualPrincipal),
      cumInterest: Math.round(cumInterest),
      closingBalance: Math.round(balance),
    });
  }

  return {
    amount: Math.round(amount),
    monthlyEmi: Math.round(monthlyEmi),
    totalInterest: Math.round(totalInterest),
    totalPayment: Math.round(totalPayment),
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('emiAmountDisplay', fmt(params.amount));
  setTxt('emiRateDisplay', `${params.rate}%`);
  setTxt('emiTenureDisplay', `${params.tenure} yrs`);
}

function renderEmiResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('emiVerdictHeadline', `Monthly EMI: ${fmt(result.monthlyEmi)}/mo`);
  setTxt('emiVerdictDetail', `For a ${fmt(result.amount)} loan at ${params.rate}% for ${params.tenure} years, your total interest payable is ${fmt(result.totalInterest)} (${((result.totalInterest / result.amount) * 100).toFixed(1)}% of principal).`);

  setTxt('emiTotalInterestValue', fmt(result.totalInterest));
  setTxt('emiTotalPaymentValue', fmt(result.totalPayment));
  setTxt('emiPrincipalMetricValue', fmt(result.amount));
  setTxt('emiInterestMetricValue', fmt(result.totalInterest));
  setTxt('emiTotalMetricValue', fmt(result.totalPayment));

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.amount, result.totalInterest);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('EMI Chart rendering error:', err);
  }
}

function renderDonutChart(principal, totalInterest) {
  const ctx = document.getElementById('emiDonutChart');
  if (!ctx) return;

  if (emiDonutChart) emiDonutChart.destroy();

  emiDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Principal Loan Amount', 'Total Interest'],
      datasets: [
        {
          data: [principal, totalInterest],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent')],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('emiGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const balanceData = schedule.map((r) => r.closingBalance);
  const interestData = schedule.map((r) => r.cumInterest);

  if (emiGrowthChart) emiGrowthChart.destroy();

  emiGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Remaining Principal Balance',
          data: balanceData,
          borderColor: cssVar('--chart-buy'),
          backgroundColor: 'rgba(52, 211, 153, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          borderWidth: 2.5,
        },
        {
          label: 'Cumulative Interest Paid',
          data: interestData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          borderWidth: 2.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('emiScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => `
    <tr>
      <td>${row.year}</td>
      <td>${fmt(row.openingBalance)}</td>
      <td>${fmt(row.annualEmi)}</td>
      <td class="advantage-buyer">${fmt(row.annualInterest)}</td>
      <td class="advantage-renter">${fmt(row.annualPrincipal)}</td>
      <td style="font-weight: 600;">${fmt(row.closingBalance)}</td>
    </tr>
  `).join('');
}

function exportEmiCsv() {
  const params = getEmiInputs();
  const result = calculateEmi(params);
  const headers = ['Year', 'Opening Principal', 'Annual EMI Paid', 'Interest Paid', 'Principal Repaid', 'Closing Balance'];
  const rows = result.schedule.map((r) => [
    r.year, r.openingBalance, r.annualEmi, r.annualInterest, r.annualPrincipal, r.closingBalance
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `emi-amortization-schedule-${params.tenure}yrs.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applyEmiCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultAmount = 2500000;
  let maxAmount = 50000000;
  let stepAmount = 50000;

  if (curr === 'INR') {
    defaultAmount = 2500000;
    maxAmount = 50000000;
    stepAmount = 50000;
  } else if (curr === 'JPY') {
    defaultAmount = 25000000;
    maxAmount = 500000000;
    stepAmount = 500000;
  }

  const aSlider = document.getElementById('emiAmount');
  if (aSlider) {
    aSlider.max = maxAmount;
    aSlider.step = stepAmount;
    aSlider.value = overrideParams.amount ?? defaultAmount;
  }

  const rSlider = document.getElementById('emiRate');
  if (rSlider && overrideParams.rate !== null) {
    rSlider.value = overrideParams.rate;
  }

  const tSlider = document.getElementById('emiTenure');
  if (tSlider && overrideParams.tenure !== null) {
    tSlider.value = overrideParams.tenure;
  }
}

function recalculateEmi() {
  const params = getEmiInputs();
  updateDisplays(params);
  updateEmiUrlParams(params);
  const result = calculateEmi(params);
  renderEmiResults(result, params);
}

function initEmiCalculator() {
  if (!document.getElementById('emiAmount')) return;

  const urlParams = parseEmiUrlParams();

  initCountry((country) => {
    applyEmiCountryRanges(country, urlParams);
    recalculateEmi();
  });

  const inputs = ['emiAmount', 'emiRate', 'emiTenure'];
  inputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', recalculateEmi);
      el.addEventListener('change', recalculateEmi);
    }
  });

  const exportBtn = document.getElementById('exportEmiCsv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportEmiCsv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initEmiCalculator();
});
